// File: src/Components/Navbar/Navbar.jsx

import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import logo1 from '../../Assets/logo.png'

const Navbar = () => {
    // State for navbar background on scroll
    const [scrolled, setScrolled] = useState(false);
    // State for mobile menu visibility
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isMobileMenuOpen]);

    // --- UPDATED NAVIGATION LINKS ---
    const navLinks = [
        { name: 'Home', path: '/' },
        {
            name: 'About Islamabad',
            path: '/about-islamabad',
            dropdown: [
                { name: 'Introduction', path: '/about-islamabad' },
                { name: 'Blue World City', path: '/blue-world-city' },
                { name: 'Residential Areas', path: '/residential-areas' },
                { name: 'Commercial Areas', path: '/commercial-areas' },
                { name: 'Sectors Overview', path: '/sectors-overview' },
                { name: 'Airport Societies', path: '/airport-societies' },
            ],
        },
        // --- UPDATED SECTION START ---
        {
            name: 'Investment Opportunities',
            path: '/investment-opportunities', // Clicking parent goes to Downtown (default)
            dropdown: [
                { name: 'Downtown', path: '/investment-opportunities#downtown' },
                { name: 'Awami Greens', path: '/investment-opportunities#awami-greens' },
                { name: 'Tourism Business District', path: '/investment-opportunities#tourism-district' },
                { name: 'General Block 2', path: '/investment-opportunities#general-block-2' },
                { name: 'Tourism Villas', path: '/investment-opportunities#tourism-villas' }, // ADDED HERE
            ],
        },
        // --- UPDATED SECTION END ---
        {
            name: 'Guides & Insights',
            path: '/housing-schemes-status',
            dropdown: [
                { name: 'Future Growth Regions', path: '/future-growth' },
                { name: 'Housing Scheme Status', path: '/housing-schemes-status' },
                { name: 'Private Societies', path: '/private-societies' },
                { name: 'Why Invest', path: '/why-invest' },
            ],
        },
        { name: 'Blue World City', path: '/dragon-mall' },
    ];

    const activeLinkStyle = {
        color: '#FFD700', 
    };

    // --- DESKTOP NAVIGATION ---
    const DesktopNav = () => (
        <div className="hidden md:flex items-center space-x-8 h-full">
            {navLinks.map((link) =>
                link.dropdown ? (
                    <div key={link.name} className="relative group h-full flex items-center">
                        <NavLink
                            to={link.path}
                            className={`font-semibold transition-colors h-full flex items-center gap-1 ${scrolled ? 'text-muted-foreground hover:text-primary' : 'text-gray-200 hover:text-white'}`}
                            style={({ isActive }) => isActive ? activeLinkStyle : undefined}
                        >
                            {link.name}
                        </NavLink>

                        <div className="absolute top-full left-0 mt-0 w-64 bg-white rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 invisible group-hover:visible pt-2 pb-2 z-10">
                            {link.dropdown.map((item) => (
                                <NavLink
                                    key={item.name}
                                    to={item.path}
                                    className="block px-4 py-2 text-muted-foreground hover:bg-background hover:text-primary"
                                >
                                    {item.name}
                                </NavLink>
                            ))}
                        </div>
                    </div>
                ) : (
                    <NavLink
                        key={link.name}
                        to={link.path}
                        className={`font-semibold transition-colors h-full flex items-center ${scrolled ? 'text-muted-foreground hover:text-primary' : 'text-gray-200 hover:text-white'}`}
                        style={({ isActive }) => isActive ? activeLinkStyle : undefined}
                        end
                    >
                        {link.name}
                    </NavLink>
                )
            )}
        </div>
    );

    // --- MOBILE NAVIGATION ---
    const MobileNav = () => {
        const [openDropdown, setOpenDropdown] = useState(null);

        const toggleDropdown = (dropdownName) => {
            setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
        };

        return (
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: '100%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '100%' }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="fixed top-0 right-0 h-full w-full bg-primary text-white z-50 p-6 md:hidden overflow-y-auto"
                    >
                        <div className="flex justify-between items-center mb-10">
                            <Link
                                to="/"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-2 font-poppins font-bold text-xl text-white"
                            >
                                <img
                                    src={logo1}
                                    alt="logo"
                                    className="w-8 h-8 object-contain"
                                />
                            </Link>
                            <button onClick={() => setMobileMenuOpen(false)}>
                                <FaTimes size={24} />
                            </button>
                        </div>

                        <ul className="flex flex-col space-y-4">
                            {navLinks.map((link) =>
                                link.dropdown ? (
                                    <li key={link.name}>
                                        <button
                                            onClick={() => toggleDropdown(link.name)}
                                            className="w-full flex justify-between items-center font-bold text-lg text-left"
                                        >
                                            {link.name}
                                            <motion.span animate={{ rotate: openDropdown === link.name ? 180 : 0 }}>
                                                ▼
                                            </motion.span>
                                        </button>
                                        <AnimatePresence>
                                            {openDropdown === link.name && (
                                                <motion.ul
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="pl-4 mt-2 space-y-2 overflow-hidden"
                                                >
                                                    {link.dropdown.map((item) => (
                                                        <li key={item.name}>
                                                            <NavLink
                                                                to={item.path}
                                                                onClick={() => setMobileMenuOpen(false)}
                                                                className="block text-gray-300 hover:text-accent"
                                                            >
                                                                {item.name}
                                                            </NavLink>
                                                        </li>
                                                    ))}
                                                </motion.ul>
                                            )}
                                        </AnimatePresence>
                                    </li>
                                ) : (
                                    <li key={link.name}>
                                        <NavLink
                                            to={link.path}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="font-bold text-lg hover:text-accent"
                                            style={({ isActive }) => isActive ? activeLinkStyle : undefined}
                                            end
                                        >
                                            {link.name}
                                        </NavLink>
                                    </li>
                                )
                            )}
                            <li>
                                <NavLink
                                    to="/contact"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="font-bold text-lg hover:text-accent"
                                    style={({ isActive }) => isActive ? activeLinkStyle : undefined}
                                >
                                    Contact
                                </NavLink>
                            </li>
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        );
    };

    return (
        <>
            <motion.nav
                className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}
                initial={{ y: -100 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="container mx-auto px-6 flex justify-between items-center h-20">
                    <Link
                        to="/"
                        className={`flex items-center gap-2 font-poppins font-bold text-xl transition-colors ${scrolled ? 'text-primary' : 'text-white'}`}
                    >
                        <img
                            src={logo1}
                            alt="InvestInISB Logo"
                            className="w-28 h-28 object-contain"
                        />
                    </Link>

                    <DesktopNav />

                    <div className="hidden md:block">
                        <Link to="/contact" className="bg-accent text-primary font-bold px-6 py-2 rounded-full hover:opacity-90 transition-opacity">
                            Consultation
                        </Link>
                    </div>

                    <div className="md:hidden">
                        <button onClick={() => setMobileMenuOpen(!isMobileMenuOpen)} className={scrolled ? 'text-primary' : 'text-white'}>
                            <FaBars size={24} />
                        </button>
                    </div>
                </div>
            </motion.nav>

            <MobileNav />
        </>
    );
};

export default Navbar;